'use client';

import { useState } from 'react';
import { createCustomer } from '@/shared/api/stripe/create-customer';
import { createSubscription } from '@/shared/api/stripe/create-subscription';
import { createSubscriptionInvoice } from '@/shared/api/stripe/create-subscription-invoice';
import { API_AUTH_REGISTRATION } from '@/shared/constants/bff-endpoints';
import { STRIPE_PRICE_IDS } from '@/entities/plan/model/stripe-price-ids';
import type { RegistrationFormData } from '@/shared/lib/validation/registration';
import type { SignupPayload } from './signup-payload';


type UseSignupResult = {
    submit: (payload: SignupPayload) => Promise<SignupPayload | null>;
    loading: boolean;
    error: string | null;
};


/**
 * Builds the flat RegistrationFormData shape expected by legacy createCustomer
 * server action from the wizard's split-form data.
 */
const toApiFormData = (payload: SignupPayload): RegistrationFormData & {
    stripe_customer_id: string
    stripe_payment_method_id: string | null
    stripe_subscription_id: string
} => ({
    email                   : payload.credentials.email,
    password                : payload.credentials.password,
    terms                   : payload.credentials.terms,
    first_name              : payload.profile.first_name,
    last_name               : payload.profile.last_name,
    job_title               : payload.profile.job_title,
    phone                   : payload.profile.phone,
    company_name            : payload.company.company_name,
    company_vat             : payload.company.company_vat,
    postal_code             : payload.company.postal_code,
    country                 : payload.company.country,
    city                    : payload.company.city,
    address                 : payload.company.address,
    billing_period          : payload.plan.billing_period,
    plan_id                 : payload.plan.plan_id,
    billing_email           : payload.billing_address.billing_email,
    billing_email_additional: payload.billing_address.billing_email_additional ?? '',
    vat_id                  : payload.billing_address.vat_id ?? '',
    billing_postal_code     : payload.billing_address.billing_postal_code,
    billing_country         : payload.billing_address.billing_country,
    billing_city            : payload.billing_address.billing_city,
    billing_address         : payload.billing_address.billing_address,
    billing_address_extra   : payload.billing_address.billing_address_extra ?? '',
    stripe_customer_id      : payload.stripe_customer_id,
    stripe_payment_method_id: payload.stripe_payment_method_id ?? null,
    stripe_subscription_id  : payload.stripe_subscription_id,
});


/**
 * Final signup orchestration:
 *   1. createCustomer (Stripe)        → customerId
 *   2. createSubscription / Invoice   → subscriptionId
 *   3. Returns full payload + Stripe IDs, ready to send to our backend
 *      (backend createAccount call will be added when API is ready).
 */
export const useSignup = (): UseSignupResult => {
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ]     = useState<string | null>(null);

    const submit = async (payload: SignupPayload): Promise<SignupPayload | null> => {
        setError(null);
        setLoading(true);

        try {
            const priceId = STRIPE_PRICE_IDS[payload.plan.plan_id]?.[payload.plan.billing_period];

            if (!priceId) throw new Error(
                `No priceId for planId=${ payload.plan.plan_id }, period=${ payload.plan.billing_period }`,
            );

            const data        = toApiFormData(payload);
            const billingName = `${ payload.profile.first_name } ${ payload.profile.last_name }`.trim();

            const { stripe_customer_id } = await createCustomer({ data, billingName });

            let stripe_subscription_id: string;

            if (payload.payment_method === 'invoice') {
                const sub              = await createSubscriptionInvoice({ stripe_customer_id, priceId });
                stripe_subscription_id = sub.stripe_subscription_id;
            } else {
                if (!payload.stripe_payment_method_id) throw new Error('Payment method ID is missing');

                const sub              = await createSubscription({
                    priceId,
                    stripe_customer_id,
                    stripe_payment_method_id: payload.stripe_payment_method_id,
                });
                stripe_subscription_id = sub.stripe_subscription_id;
            }

            const result: SignupPayload = {
                ...payload,
                stripe_customer_id,
                stripe_subscription_id,
            };

            const res = await fetch(API_AUTH_REGISTRATION, {
                method : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body   : JSON.stringify(toApiFormData(result)),
            });

            const backendResult = await res.json();

            if (!res.ok) throw new Error(backendResult.error ?? 'Backend signup failed');

            return result;
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Signup failed. Please try again.';
            setError(message);
            return null;
        } finally {
            setLoading(false);
        }
    };

    return { submit, loading, error };
};
